import {Observable} from "rxjs";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {Injectable, ApplicationRef, ChangeDetectorRef} from "@angular/core";
import {Observer} from "rxjs";
import {ContentCursor, AllowedSelectionType} from "../content/content.cursor";
import {BugsnagLoggingService} from "../logging/bugsnag-logging.service";
import {RecordingComparisonService, IRecordingComparisonResult} from "./recording-comparison.service";
import {RecognizerService, IRecognizedSequence} from "../recognition/recognizer.service";
import {CssSentence, CssWord, ContentCssClass} from "../../content/content-view.objects";
import {InterventionState, BaseInterventionState, AudioInterventionState, VisualInterventionState,
    MockInterventionState, TimingParameters, MicrophoneCheckInterventionState} from "./recording.interventions.state";
import {IExportDictionary} from "../../api/data-model";
import {RecordingObjects, MicrophoneCheckData, BookSessionData} from "./recording.objects";
import {SessionService} from "../configuration/session.service";
import {IAudioData} from "../audio/microphone.service";
import {MicrophoneCheckEvent, MICROPHONECHECK_STATUS} from "../../event/MicrophoneCheckEvent";
import {MicrophoneStatus} from "../audio/microphone.service";
import {EventBus} from "../../event/EventBus";
import {
    CountdownStartEvent, CountdownStopEvent, PauseSelectionEvent,
    RecordingStatusEvent, RECORDING_STATUS
} from "../../event/ReadAndRecordEvents";
import {UserModelChangedEvent} from "../../event/UserModelEvents";
import {ContentFormatter} from "../../content/content.formatter";
import {UserModelService} from "../usermodel/usermodel.service";
import {IReadingMode} from "../../reading/reading.component";
import {ClientConfiguration} from "../configuration/client.config";
import {StudentConfiguration} from "../assignment/student.configuration";
import {ModalConfig} from "../notification/modal.config";
import {AudioQueueService} from "../audio-playback/audioqueue.service";
import {AudioQueueItem, TranslateAudio} from "../audio-playback/audioqueue.item";
import {AudioQueueEvent, InterruptAudio} from "../../event/AudioQueueEvent";
import {filter, share} from "rxjs/operators";
import {ErrorType} from "../../constants";

export enum RecordingServiceStatus {
    NOT_INITIALIZED = 0,
    INITIALIZED = 1,
    RECOGNIZER_INITIALIZED = 2,
    SENTENCE_COMPLETED = 3,
    SENTENCE_INCOMPLETE = 4,
    SENTENCE_TIMEOUT = 5,
    MICROPHONE_CHECK_REQUESTED = 6,
    MICROPHONE_CHECK_READY = 7,
    MICROPHONE_CHECK_SENTENCE_COMPLETED = 8,
    MICROPHONE_CHECK_SENTENCE_INCOMPLETE = 9,
    MICROPHONE_CHECK_SENTENCE_TIMEOUT = 10,
}

@Injectable()
export class RecordingService implements IReadingMode {

    public recordingServiceStatus:Observable<RecordingServiceStatus>;
    public sentenceFinishedCallback:Function;
    private _changeDetectorRef: ChangeDetectorRef = null;

    private _recordingServiceStatusObserver:Observer<RecordingServiceStatus>;
    private _wordWallActive = false;

    private currentRecordingServiceStatus:RecordingServiceStatus = RecordingServiceStatus.NOT_INITIALIZED;

    private timingParameters:TimingParameters;
    private currentInterventionState:InterventionState = null;
    private baseInterventionState:InterventionState = null;
    private recordingObjects:RecordingObjects = null;

    private visualWordArray:Array<CssWord> = [];

    private microphoneCheckData:MicrophoneCheckData = new MicrophoneCheckData();

    private _isCountdownRun:boolean = false;
    private _currentlyRecording:boolean = false;
    private waitingForCountSentence:CssSentence = null;
    private interventionTimeStart:number;

    private inactiveModalTriggerTime:number = 30000;
    private totalInterventionTime: number = 0;
    private recordingComparisonResult:IRecordingComparisonResult = {id: "", resultString: "", resultDetail: [],
                                                                    multiWordInternalProgress: false,
                                                                    accelerateIntervention: false};

    constructor(private recognizerService:RecognizerService,
                private recordingComparisonService:RecordingComparisonService,
                private sessionService:SessionService,
                private log:BugsnagLoggingService,
                private eventBus: EventBus,
                private contentCursor:ContentCursor,
                private userModelService:UserModelService,
                private audioQueueService:AudioQueueService,
                private config:ClientConfiguration,
                private studentConfig:StudentConfiguration,
                private modalConfig: ModalConfig) {

        this.log.log("RECORDING SERVICE INIT");

        this.recognizerService.recognizerResultObservable.subscribe(data => {
            this.onRecognizerData(data);
        });

        this.recordingServiceStatus = new Observable<RecordingServiceStatus>(observer => {
            this._recordingServiceStatusObserver = observer;
        }).pipe(share());

        this.recordingServiceStatus.subscribe((event) => {
            this.log.log("RecordingService status: " + RecordingServiceStatus[event]);
        });

        if (!this.recognizerService.isMock) {
            this.recognizerService.microphoneStatusObservable.subscribe(status => {
                // send recording service status
                if (status === MicrophoneStatus.MIC_REQUESTED) {
                    this._recordingServiceStatusObserver.next(RecordingServiceStatus.MICROPHONE_CHECK_REQUESTED);
                }
                if (status === MicrophoneStatus.READY) {
                    this._recordingServiceStatusObserver.next(RecordingServiceStatus.MICROPHONE_CHECK_READY);
                }
            });
        }

        this._recordingServiceStatusObserver.next(RecordingServiceStatus.RECOGNIZER_INITIALIZED);
        this.currentRecordingServiceStatus = RecordingServiceStatus.RECOGNIZER_INITIALIZED;

        this.recordingObjects = new RecordingObjects();
        // need a default initialization of timing and interventions for mic check
        let level = 2; // reading level
        let waitTime = 1; // default wait time setting
        this.timingParameters = TimingParameters.build(level, waitTime);
        this.buildStates();

        this.eventBus.pipe(filter(
            event => event instanceof UserModelChangedEvent)).subscribe((event:UserModelChangedEvent) => {
                if (event.delta.justCompletedRecording) {
                    this.log.log("calling format colors from RecordingService usermodel callback");
                    this.formatSelectionColors();

                }
        });

        this.eventBus.pipe(filter(
            (e:CountdownStopEvent) => e.type === CountdownStopEvent.TYPE)).subscribe((e:CountdownStopEvent) => {
            this.startAfterCountdown();
        });

        this.eventBus.pipe(filter(
            (e:MicrophoneCheckEvent) => e.type === MicrophoneCheckEvent.TYPE)).subscribe((e:MicrophoneCheckEvent) => {
            if (e.status ===  MICROPHONECHECK_STATUS.success) {
                if (!this.microphoneCheckData.microphoneCheckInitiated) {
                    this.microphoneCheckData.microphoneCheckInitiated = true;
                    this.recognizerService.startContinuousMicCheck();
                    if (this.microphoneCheckData.temporarySentence != null) {
                        this.startSentence(this.microphoneCheckData.temporarySentence, this.sentenceFinishedCallback);
                    }
                }
            }
            if (e.status ===  MICROPHONECHECK_STATUS.cancel) {
                // If the user cancels, need to start the continuous check since the audio device has been selected
                this.recognizerService.startContinuousMicCheck();
            }
        });
    }


    private setInterventionTiming():void {
        let waitTimeSetting = this.studentConfig.interventionWaitTime - 1;
        let level = this.getNumericGradeEquivalent();
        this.log.log("wait time setting:" + waitTimeSetting + " grade level: " + level);
        this.timingParameters = TimingParameters.build(level, waitTimeSetting);
        this.buildStates();
    }

    private getNumericGradeEquivalent():number {
        let value = Number(this.contentCursor.selection.metaData.ATOSGradeEquivalent);
        if (Number.isNaN(value)) {
            value = 1;
            if (this.contentCursor.selection.metaData.poetryLevel) {
                value = Number(this.contentCursor.selection.metaData.poetryLevel.ATOSGradeEquivalent);
            }
        }
        this.log.log("grade equiv for wait time: " + value);
        return value;
    }

    private buildStates(): void {

        let baseInterventionState = null;
        if (this.recognizerService.isMock) {
            baseInterventionState = new MockInterventionState(this.timingParameters);
        } else {
            baseInterventionState = new BaseInterventionState(this.timingParameters);
        }

        baseInterventionState.status.subscribe(() => {
            this.log.log("onBaseStateChange: subscribe");
            this.onBaseStateChange(true);

        });

        let visualInterventionState = new VisualInterventionState(this.timingParameters);
        visualInterventionState.status.subscribe(() => {
            this.log.log("onVisualStateChange: subscribe");
            this.onVisualStateChange(true);

        });
        baseInterventionState.setNextState(visualInterventionState);

        let audioInterventionState = new AudioInterventionState(this.timingParameters);
        audioInterventionState.status.subscribe(() => {
            this.log.log("onAudioStateChange: subscribe");
            this.onAudioStateChange();
        });
        visualInterventionState.setNextState(audioInterventionState);
        this.baseInterventionState = baseInterventionState;
        this.currentInterventionState = this.baseInterventionState;
    }

    private onRecognizerData(recognized:IRecognizedSequence):void {

        let logString = "";
        recognized.data.forEach((recognizedItem) => {
            logString += recognizedItem.word + ":" + recognizedItem.end.toString() + " ";
        });
        if (this.microphoneCheckData.isMicrophoneCheck) {
            this.log.debug("calling microphone check with result: " + logString);
            this.onMicrophoneCheck(recognized);
        } else if (!this._wordWallActive) {
            if (this.recognizerService.isMock) {
                this.onRecognizeMock(recognized);
            } else if (this.contentCursor.selectedWord && this._currentlyRecording) {
                this.onRecognize(recognized);
            }
        }
    }

    private onMicrophoneCheck(recognized:IRecognizedSequence):void {
        this.microphoneCheckData.microphoneCheckCorrectCount = 0;
        recognized.data.forEach((recognizedItem) => {
            this.microphoneCheckData.microphoneCheckSentence.words.forEach((word) => {
                if (word.word === recognizedItem.word) {
                    this.microphoneCheckData.microphoneCheckCorrectCount++;
                }
            });
        });

        if (this.microphoneCheckData.microphoneCheckCorrectCount >=
            (this.microphoneCheckData.microphoneCheckSentence.words.length / 2)) {
            this.microphoneCheckData.microphoneCheckPassed = true;
        }
    }

    private onRecognize(recognized: IRecognizedSequence): void {
        // Normal audio comparison
        this.recordingComparisonResult = this.recordingComparisonService.getComparisonResult(recognized);

        let wordId:string = this.recordingComparisonResult.id;
        let endTime:string = recognized.data.length ? recognized.data[recognized.data.length - 1].end.toString() : "";
        this.log.log("recognition comparison: " + this.recordingComparisonResult.resultString + "end time:" + endTime);
        let lastWordRecognized:CssWord = this.contentCursor.selectedSentence.getChildFromId(wordId);
        let previousCursorWord:CssWord = this.contentCursor.selectedWord;
        if (lastWordRecognized) {
            this.contentCursor.selectWord(lastWordRecognized); // should also set new sentence if needed
            this.contentCursor.goNextWord(); // cursor is the word we are waiting to hear next,
                                             // i.e. the word after the recognized word
        }

        let atEndOfSentence:boolean = !!lastWordRecognized &&
                                      (!this.contentCursor.selectedWord ||
                                       (this.contentCursor.selectedWord.parentSentence() !==
                                        lastWordRecognized.parentSentence()));
        let cursorMovedForward = !atEndOfSentence &&
                                     (this.contentCursor.selectedSentence.indexOfChild(this.contentCursor.selectedWord)
                                      > this.contentCursor.selectedSentence.indexOfChild(previousCursorWord));

        if (cursorMovedForward || this.recordingComparisonResult.multiWordInternalProgress || atEndOfSentence) {
            let time = Date.now() - this.interventionTimeStart;
            this.totalInterventionTime = (this.totalInterventionTime - time < 0) ? 0 :
                                         this.totalInterventionTime - time;
        }

        if (atEndOfSentence) {
            this.log.log("moving forward, received last word of sentence: " + lastWordRecognized.word);
            this.currentInterventionState.stop();
            this.finishSentence(lastWordRecognized.parentSentence(), !this.contentCursor.selectedWord);
            this.cancelInterventionFeedback();
            if (!this.contentCursor.selectedWord) { // end of selection
                this.eventBus.next(new PauseSelectionEvent()); // need to automatically trigger pause
            }

        } else {
            if (cursorMovedForward || this.recordingComparisonResult.multiWordInternalProgress) {
                // cursor moved forward, or we recognized a new word in a multi-word entry
                // reset timers and reset to base state
                // this.log.log("recognition comparison: " + this.recordingComparisonResult.resultString);
                if (lastWordRecognized) {
                    this.log.log("moving forward in sentence, received word " + lastWordRecognized.word);
                    if (this.recordingComparisonResult.multiWordInternalProgress) {
                        this.log.log("moving forward due to multiword progress");
                    }
                }
                if (cursorMovedForward) {
                    this.cancelInterventionFeedback();
                }
                this.currentInterventionState.stop();
                this.resetInterventionState();

            } else {
                if (this.recordingComparisonResult.accelerateIntervention &&
                    (this.currentInterventionState === this.baseInterventionState)) {
                    // generate visual intervention
                    this.log.log("accelerated intervention on word " + this.contentCursor.selectedWord.word);
                    this.log.log("recognition comparison: " + this.recordingComparisonResult.resultString);
                    this.onBaseStateChange(true);
                }
            }
        }

    }

    private onRecognizeMock(recognized:IRecognizedSequence):void {

        let recordingComparisonResult:IRecordingComparisonResult =
            this.recordingComparisonService.getComparisonResult(recognized);
        let wordId:string = recordingComparisonResult.id;
        this.log.log("word id from comparison: " + wordId);
        let lastWordRecognized:CssWord = this.contentCursor.selectedSentence.getChildFromId(wordId);
        if (lastWordRecognized) {
            this.contentCursor.selectWord(lastWordRecognized); // should also set new sentence if needed
            this.contentCursor.goNextWord(); // cursor is the word we are waiting to hear next,
                                             // i.e. the word after the recognized word
        }

        let atEndOfSentence:boolean = !!lastWordRecognized && (!this.contentCursor.selectedWord ||
                                                               (this.contentCursor.selectedWord.parentSentence()
                                                                !== lastWordRecognized.parentSentence()));

        if (recognized.endOfFile) {
            this.log.log("pausing automated test, end of file recognition reached");

            this.sentenceFinishedCallback(recordingComparisonResult);
            this.cancelInterventionFeedback();
            this.pause();
        } else {
            if (atEndOfSentence && lastWordRecognized) {
                this.contentCursor.selectWord(lastWordRecognized);
            }
        }

    }

    private finishSentence(sentence: CssSentence, isEndOfSelection: boolean): void {
        this.reattachUIAfterRecording();
        sentence.words.forEach(word => word.removeCssClass(ContentCssClass.WORD_RECOGNIZED));
        // not sure if these are the right options for stop . . .
        this.recordingObjects.addWordRecordData(this.recordingComparisonService.sentenceTextData);
        this.recognizerService.requestRecording((data: IAudioData) => {

            let uploadData: BookSessionData = this.recordingObjects.createUploadData(sentence, data, isEndOfSelection);

            this.log.info("isEndOfSelection:" + isEndOfSelection
                          + " recordingState:" + JSON.stringify(uploadData.learnerStory) + " audio len:" + data.length);

            this.sessionService.sendBookSessionData(uploadData,
                                                    this.onRecordUploaded, this.onRecordUploadError).subscribe((s) => {
                this.log.info("upload event: " + s);
            });
        }, "base64");
        this.recognizerService.stop({catchErr: false, clear: false, continuousRecording: true});
        this.sentenceFinishedCallback();
        this._recordingServiceStatusObserver.next(RecordingServiceStatus.SENTENCE_COMPLETED);
    }

    onRecordUploaded = (data) => {
        // RA.userData.isPurge = false; // TODO: why was this needed in V1?
        this.log.log("uploaded record " + JSON.stringify(data));
    };

    onRecordUploadError = () => {
        this.pause();
        this.modalConfig.openMessageModal("upload_error");
        this.log.error("Failed to upload recording data", ErrorType.CONNECTION);
    };

    private onBaseStateChange(addIntervention:boolean):void {
        this.processIntervention(addIntervention, false,
                                 "Moving from base state: timer expired or accelerated intervention ");

        this.applyVisualFeedback();
        this.interventionTimeStart = this.currentInterventionState.start();
        this.recognizerService.restartFrom(this.contentCursor.selectedWord.id, this.contentCursor.selectedSentence,
                                           false, this.studentConfig.pronunciationCorrectness);
    }

    private onVisualStateChange(addIntervention:boolean):void {
        this.processIntervention(addIntervention, true, "Moving from visual state: timer expired ");
        this.playAudioInterventionFile();
    }

    private onAudioStateChange():void {
        this.processIntervention(true, true, "Moving from audio state and skipping word: timer expired ");
        if (this.totalInterventionTime >= this.inactiveModalTriggerTime) {
            this.launchInactiveMessage();
            return;
        }
        this.cancelInterventionFeedback();
        let currentWord = this.contentCursor.selectedWord;
        this.contentCursor.goNextWord();
        if (!this.contentCursor.selectedWord ||
            (currentWord.parentSentence() !== this.contentCursor.selectedSentence)) {
            this.currentInterventionState.stop();
            this.finishSentence(currentWord.parentSentence(), !this.contentCursor.selectedWord);
        }
        if (this.contentCursor.selectedWord) {
            if (this.contentCursor.selectedWord.isGlue()) {
                // highlight, trigger transition from visual to audio state so that audio plays
                this.applyVisualFeedback();
                this.currentInterventionState = this.baseInterventionState.getNextState();
                this.onVisualStateChange(false);
            } else {
                // highlight, go to visual state
                this.currentInterventionState = this.baseInterventionState;
                this.onBaseStateChange(false);
            }
        } else { // skipping last word of selection
            this.eventBus.next(new PauseSelectionEvent()); // need to automatically trigger pause
        }
    }

    private processIntervention(recordIntervention: boolean, isAudioIntervention: boolean, description: string):void {
        let time = Date.now() - this.interventionTimeStart;
        this.totalInterventionTime += time;
        this.log.log(description + time + " ms");
        this.log.log("intervention word " + this.contentCursor?.selectedWord?.word);
        this.log.log("most recent recognition comparison: " + this.recordingComparisonResult.resultString);
        this.currentInterventionState.stop();
        if (recordIntervention && this.contentCursor.selectedWord) {
            this.recordingComparisonService.addIntervention(this.contentCursor.selectedWord.id, isAudioIntervention);
        }
        if (this.currentInterventionState.getNextState()) {
            this.currentInterventionState = this.currentInterventionState.getNextState();
            this.log.log("Moving to new intervention state:" + this.currentInterventionState.name());
        }
    }

    private detachUIBeforeRecording(): void {
        if (this._changeDetectorRef) {
            this._changeDetectorRef.detectChanges();
            this._changeDetectorRef.detach();
        }
    }

    private updateUIDuringRecording(): void {
        if (this._changeDetectorRef) {
            this._changeDetectorRef.reattach();
            this._changeDetectorRef.detectChanges();
            this._changeDetectorRef.detach();
        }
    }

    private reattachUIAfterRecording(): void {
        if (this._changeDetectorRef) {
            this._changeDetectorRef.reattach();
            this._changeDetectorRef.detectChanges();
        }
    }

    private applyVisualFeedback(): void {
        this.visualWordArray.push(this.contentCursor.selectedWord);
        this.contentCursor.selectedWord.addCssClass(ContentCssClass.WORD_INTERVENTION_VISUAL);
        let interventionIndex = this.contentCursor.selectedSentence.words.indexOf(this.contentCursor.selectedWord);
        this.contentCursor.selectedSentence.words.filter((word, index, words) => index < interventionIndex)
            .forEach(word => word.addCssClass(ContentCssClass.WORD_RECOGNIZED));
        this.updateUIDuringRecording();
    }

    private removeVisualFeedback():void {
        if (this.contentCursor.selectedSentence) {
            this.contentCursor.selectedSentence.words.forEach(
                word => word.removeCssClass(ContentCssClass.WORD_RECOGNIZED));
        }
        for (let word of this.visualWordArray) {
            word.removeCssClass(ContentCssClass.WORD_INTERVENTION_VISUAL);
        }
        this.visualWordArray = [];
        this.updateUIDuringRecording();
    }

    onAudioFinished = () => {
        this.recognizerService.unmute();
        if (this.recognizerService.isRecording()) {
            this.log.log("onVisual change, calling start on " + this.currentInterventionState.name());
            this.interventionTimeStart = this.currentInterventionState.start();
            this.recognizerService.restartFrom(this.contentCursor.selectedWord.id, this.contentCursor.selectedSentence,
                                               true, this.studentConfig.pronunciationCorrectness);
        } else {
            this.log.log("recording is paused, will not restart intervention timer");
        }
    }

    private cancelInterventionFeedback():void {
        for (let word of this.visualWordArray) {
            word.removeCssClass(ContentCssClass.WORD_INTERVENTION_VISUAL);
        }
        if (this.visualWordArray.length > 0) {
            this.updateUIDuringRecording();
        }
        this.visualWordArray = [];
        if (this.recognizerService.isMute) {
            this.audioQueueService.hardReset();
            this.onAudioFinished();
        }
    }

    private playAudioInterventionFile(): void {
        this.recognizerService.mute();
        // either completion of audio, pause, or error should trigger onAudioFinished
        if (this.contentCursor.selectedWord.isGlue()) {
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem("RA_STEP_2_09", TranslateAudio.YES, this.onAudioFinished, this.onAudioFinished)
            ], InterruptAudio.YES));
        } else {
            let mp3Path: string = this.config.audioUrl + this.contentCursor.selectedWord.audio;
            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem(mp3Path, TranslateAudio.NO, this.onAudioFinished, this.onAudioFinished)
                ], InterruptAudio.YES));
        }
    }

    private launchInactiveMessage(): void {
        this.log.log("total intervention time " + this.totalInterventionTime.toString());
        this.totalInterventionTime = 0;
        this.modalConfig.openMessageModal("record_timeout");
        this.removeVisualFeedback();
        this.eventBus.next(new PauseSelectionEvent());
    }

    public startCountdown():void {
        this.log.log("starting countdown");
        this.eventBus.next(new CountdownStartEvent());
    }

    public startAfterCountdown():void {
        this.log.log("starting after Countdown, story sentence " + this.waitingForCountSentence.id);
        this._currentlyRecording = true;
        this._isCountdownRun = true;
        this.contentCursor.selectWord(this.waitingForCountSentence.firstChild());
        this.recordSentence(this.waitingForCountSentence);
    }

    // TODO: current word/sentence should really be synched via content cursor
    public recordSentence(sentence:CssSentence):void {
        this.detachUIBeforeRecording();
        this.resetInterventionState();
        this.recordingComparisonService.createSentence(sentence, sentence.dictionary());
        this.log.log("recordSentence calling start on " + this.currentInterventionState.name()
                     + " pronunciationCorrectness: " + this.studentConfig.pronunciationCorrectness);
        this.recognizerService.start(sentence, this.studentConfig.pronunciationCorrectness, null);
    }

    private resetInterventionState():void {
        this.currentInterventionState = this.baseInterventionState;
        this.interventionTimeStart = this.currentInterventionState.start();
        this.startRecordingMode();
    }

    public startWord(word:CssWord):void {
    }

    public startSentence(sentenceItem:CssSentence, callback:Function):void {
        this.log.log("calling startSentence on " + sentenceItem.id);
        if (this.microphoneCheckData.microphoneCheckInitiated) {
            this.log.log("microphone check is active, setting waitingForCountSentence");
            this.sentenceFinishedCallback = callback;
            this.waitingForCountSentence = sentenceItem;
            if (!this._isCountdownRun) {
                this.startCountdown();
            } else {
                this.startAfterCountdown();
            }

        } else {
            this.log.log("microphone check not active, setting microphoneCheckData.temporarySentence");
            this.microphoneCheckData.temporarySentence = sentenceItem;
            this.sentenceFinishedCallback = callback;
            this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.automatic));
        }
    }

    public activate(isPipeline: boolean):void {
        this.setInterventionTiming();
        this._isCountdownRun = false;
        this.recordingObjects.clearWordRecordListQueue();
        this.formatSelectionColors();
        this.startRecordingMode();
        this.eventBus.next(new RecordingStatusEvent(RECORDING_STATUS.start));
    }

    public pause():void {
        if (this.contentCursor.selectedSentence) {
            let word:CssWord = this.contentCursor.selectedSentence.firstChild();
            this.log.log("Setting cursor to word:" + word.word);
            this.contentCursor.selectWord(word);
        } else {
            this.log.log("at end of selection, no selected sentence");
            this.contentCursor.selectWord(this.contentCursor.firstWord);
        }
        this._currentlyRecording = false;
        this.totalInterventionTime = 0;
        this.removeVisualFeedback();
        this.recognizerService.unmute();
        this.recognizerService.stop({catchErr: false, clear: true, continuousRecording: false});
        this.currentInterventionState.stop();
        this._isCountdownRun = false;
        this.eventBus.next(new RecordingStatusEvent(RECORDING_STATUS.stop));
        this.log.log("finished pause");
        this.reattachUIAfterRecording();
    }

    public requestMicrophone():void {
        let alreadyInitialized:boolean = this.recognizerService.requestMicrophone();
        if (alreadyInitialized) {
            this._recordingServiceStatusObserver.next(RecordingServiceStatus.MICROPHONE_CHECK_READY);
        }
    }

    public initPronunciationDictionary(pronunciationDictionary: IExportDictionary): Observable<boolean> {
        return this.recognizerService.addDictionary(pronunciationDictionary);
    }

    public startMicrophoneCheck(sentence:CssSentence, volumeCallback:Function):void {
        this.detachUIBeforeRecording();
        let microphoneCheckIntervention = new MicrophoneCheckInterventionState(this.timingParameters);
        microphoneCheckIntervention.status.subscribe(() => {
            if (this.microphoneCheckData.microphoneCheckPassed) {
                this.log.log("Completed microphone check.");
                this._recordingServiceStatusObserver.next(RecordingServiceStatus.MICROPHONE_CHECK_SENTENCE_COMPLETED);
            } else {
                this.log.log("Microphone check failed.");
                this._recordingServiceStatusObserver.next(RecordingServiceStatus.MICROPHONE_CHECK_SENTENCE_TIMEOUT);
            }
            this.stopMicrophoneCheck();
        });
        this.microphoneCheckData.start(sentence, microphoneCheckIntervention);
        this.log.log("calling startMicrophoneCheck start recognition");
        this.recognizerService.start(sentence, 50, volumeCallback);
    }

    private stopMicrophoneCheck():void {
        this.microphoneCheckData.stop();
        this.recognizerService.stop({catchErr: false, clear: true, continuousRecording: false});
        this.recordingObjects = new RecordingObjects();
        this.reattachUIAfterRecording();
    }

    private startRecordingMode(): void {
        this.log.log("Starting recording view mode...");
        this.contentCursor.setMode(ContentCssClass.MODE_RECORDING);
    }

    private formatSelectionColors(): void {
        ContentFormatter.formatSelectionFromRecordReadingStates(this.contentCursor.selection,
                                                                this.userModelService.recordReadingStates);
    }

    public set changeDetectorRef(ref: ChangeDetectorRef) {
        this._changeDetectorRef = ref;
    }

    public get microphoneCheckCompleted(): boolean {
        return this.microphoneCheckData.microphoneCheckInitiated;
    }

    public set microphoneCheckCompleted(value: boolean) {
        this.microphoneCheckData.microphoneCheckInitiated = value;
    }

    public get microphoneCheckActive(): boolean {
        return this.microphoneCheckData.isMicrophoneCheck;
    }

    public getStatus():RecordingServiceStatus {
        return this.currentRecordingServiceStatus;
    }

    public setMicrophoneCheckData(microphoneCheckData:MicrophoneCheckData):void {
        this.microphoneCheckData = microphoneCheckData;
    }

    public get changeUIWhenActive():boolean {
        return this._currentlyRecording;
    }

    public get isCountdownRun():boolean {
        return this._isCountdownRun;
    }

    public get allowedSelectionType(): AllowedSelectionType {
        return AllowedSelectionType.NONE;
    }

    public get recordingServiceStatusObserver(): Observer<RecordingServiceStatus> {
        return this._recordingServiceStatusObserver;
    }

    public get wordWallActive(): boolean {
        return this._wordWallActive;
    }

    public set wordWallActive(isActive: boolean) {
        this._wordWallActive = isActive;
    }

    public set currentlyRecording(isRecording: boolean) {
        this._currentlyRecording = isRecording;
    }

}
